import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['input', 'results', 'resultItem', 'loading', 'noResults']
  static values = {
    url: String,
    debounce: { type: Number, default: 300 },
    minChars: { type: Number, default: 2 }
  }

  connect() {
    this.searchTimeout = null
    this.currentIndex = -1
    this.abortController = null
  }

  disconnect() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
    if (this.abortController) {
      this.abortController.abort()
    }
  }

  search(event) {
    const query = event.target.value.trim()

    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }

    if (query.length < this.minCharsValue) {
      this.hideResults()
      return
    }

    this.searchTimeout = setTimeout(() => {
      this.performSearch(query)
    }, this.debounceValue)
  }

  async performSearch(query) {
    if (this.abortController) {
      this.abortController.abort()
    }

    this.abortController = new AbortController()
    
    try {
      this.showLoading()
      
      const response = await fetch(`${this.urlValue}?q=${encodeURIComponent(query)}`, {
        headers: {
          'Accept': 'text/vnd.turbo-stream.html, text/html',
          'X-Requested-With': 'XMLHttpRequest'
        },
        signal: this.abortController.signal
      })

      if (!response.ok) throw new Error('Search request failed')

      const html = await response.text()
      this.updateResults(html)
      this.showResults()
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Search error:', error)
        this.showNoResults()
      }
    } finally {
      this.hideLoading()
    }
  }

  updateResults(html) {
    if (this.hasResultsTarget) {
      this.resultsTarget.innerHTML = html
      this.currentIndex = -1
    }
  }

  showResults() {
    if (this.hasResultsTarget) {
      this.resultsTarget.classList.remove('hidden')
      this.resultsTarget.setAttribute('aria-expanded', 'true')
    }
  }

  hideResults() {
    if (this.hasResultsTarget) {
      this.resultsTarget.classList.add('hidden')
      this.resultsTarget.setAttribute('aria-expanded', 'false')
    }
    this.currentIndex = -1
  }

  showLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove('hidden')
    }
    if (this.hasNoResultsTarget) {
      this.noResultsTarget.classList.add('hidden')
    }
  }

  hideLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add('hidden')
    }
  }

  showNoResults() {
    if (this.hasNoResultsTarget) {
      this.noResultsTarget.classList.remove('hidden')
    }
    this.showResults()
  }

  navigate(event) {
    if (!this.hasResultsTarget || this.resultsTarget.classList.contains('hidden')) {
      return
    }

    const items = this.resultItemTargets

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        this.currentIndex = Math.min(this.currentIndex + 1, items.length - 1)
        this.highlightItem(items)
        break
      case 'ArrowUp':
        event.preventDefault()
        this.currentIndex = Math.max(this.currentIndex - 1, -1)
        this.highlightItem(items)
        break
      case 'Enter':
        event.preventDefault()
        if (this.currentIndex >= 0 && items[this.currentIndex]) {
          const link = items[this.currentIndex].querySelector('a')
          if (link) link.click()
        }
        break
      case 'Escape':
        event.preventDefault()
        this.hideResults()
        this.inputTarget.blur()
        break
    }
  }

  highlightItem(items) {
    items.forEach((item, index) => {
      if (index === this.currentIndex) {
        item.classList.add('bg-gray-100', 'dark:bg-gray-700')
        item.setAttribute('aria-selected', 'true')
        item.scrollIntoView({ block: 'nearest' })
      } else {
        item.classList.remove('bg-gray-100', 'dark:bg-gray-700')
        item.setAttribute('aria-selected', 'false')
      }
    })
  }

  clickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.hideResults()
    }
  }

  focus() {
    this.inputTarget.focus()
  }

  clear() {
    this.inputTarget.value = ''
    this.hideResults()
  }
}